import { useSyncExternalStore } from 'react';
import type { Zakazka } from '../api/types.js';

/**
 * Globální přepínač zakázky kanceláře (UX audit B1.2 / TOP 5): jeden kontext
 * v hlavičce Layoutu místo pěti nezávislých selectů a tichého „první zakázka".
 * Volba se drží v localStorage (přežije reload i odhlášení) a stránky ji čtou
 * hookem `useVybranaZakazka`. Terén má vlastní výběr — tenhle store nečte.
 */

const KLIC = 'helfr.zakazkaId';

type Posluchac = () => void;
const posluchaci = new Set<Posluchac>();

function prectiUlozenou(): string {
  try {
    return localStorage.getItem(KLIC) ?? '';
  } catch {
    return '';
  }
}

/** Nastaví globální zakázku (persistuje) a upozorní všechny odběratele. */
export function nastavGlobalniZakazku(id: string): void {
  try {
    if (id) localStorage.setItem(KLIC, id);
    else localStorage.removeItem(KLIC);
  } catch {
    // localStorage nedostupné (private mode) — přepínač funguje jen v paměti renderu.
  }
  for (const p of posluchaci) p();
}

function odebirej(cb: Posluchac): () => void {
  posluchaci.add(cb);
  return () => posluchaci.delete(cb);
}

/** Surové uložené id (může ukazovat na už neexistující zakázku). */
export function useGlobalniZakazkaId(): string {
  return useSyncExternalStore(odebirej, prectiUlozenou, () => '');
}

/**
 * Vyřeší globální volbu proti načtenému seznamu zakázek: neplatné/prázdné id
 * spadne na první zakázku (a to VIDITELNĚ — hodnota je v hlavičce, žádný tichý
 * výběr na pozadí).
 */
export function useVybranaZakazka(zakazky: Zakazka[] | undefined): {
  vybrana: Zakazka | undefined;
  vybranaId: string;
  nastav: (id: string) => void;
} {
  const globalniId = useGlobalniZakazkaId();
  const vybrana = zakazky?.find((z) => z.id === globalniId) ?? zakazky?.[0];
  return { vybrana, vybranaId: vybrana?.id ?? '', nastav: nastavGlobalniZakazku };
}
